/**
 * The links opened last, detail and text copy, so opening one again (j/k
 * back and forth, Back to it) shows it at once while it is read again behind.
 *
 * Module-level memory only, and small: the service worker keeps the offline
 * copies. A page load (sign-out is one) starts it empty.
 */

import type { LinkDetail } from '@satisa-memory/shared'

export interface LinkText {
  mime: string
  capturedAt: string
  text: string
}

interface Entry {
  link: LinkDetail
  /** null: none read yet, or the link has none. */
  text: LinkText | null
}

export const LINK_CACHE_SIZE = 20

/** Oldest first: a Map keeps insertion order, and a read moves an entry to the end. */
const entries = new Map<string, Entry>()

export function cachedLink(id: string): Entry | undefined {
  const e = entries.get(id)
  if (e) {
    entries.delete(id)
    entries.set(id, e)
  }
  return e
}

/** Keep a link as just read; its text copy stays unless one is given. */
export function keepLink(link: LinkDetail, text?: LinkText | null) {
  const prev = entries.get(link.id)
  entries.delete(link.id)
  entries.set(link.id, { link, text: text === undefined ? (prev?.text ?? null) : text })
  for (const oldest of entries.keys()) {
    if (entries.size <= LINK_CACHE_SIZE) break
    entries.delete(oldest)
  }
}

export function keepText(id: string, text: LinkText | null) {
  const e = entries.get(id)
  if (e) e.text = text
}

export function forgetLink(id: string) {
  entries.delete(id)
}

/** The same copy read again: kept as is, so nothing re-renders. */
export function sameText(a: LinkText | null, b: LinkText | null): boolean {
  return !!a && !!b && a.mime === b.mime && a.capturedAt === b.capturedAt && a.text === b.text
}
